import { CircularProgress } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Hr from "@saleor/components/Hr";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";
interface FormData {
  allMalls: boolean;
  malls: string[];
}

export interface AssignMallDialogProps extends FetchMoreProps {
  confirmButtonState: ConfirmButtonTransitionState;
  malls: SearchMalls_search_edges_node[];
  fetchMoreMalls?: FetchMoreProps;
  field: string;
  initial: any;
  open: boolean;
  onFetch: (value: string) => void;
  onClose: () => void;
  onConfirm: (data: SearchMalls_search_edges_node[]) => void;
}

const useStyles = makeStyles(
  theme => ({
    checkboxCell: {
      paddingLeft: 0
    },
    container: {
      maxHeight: 400
    },
    heading: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    wideCell: {
      width: "100%"
    }
  }),
  { name: "AssignMallDialog" }
);

const AssignMallDialog: React.FC<AssignMallDialogProps> = props => {
  const {
    confirmButtonState,
    field,
    onClose,
    malls,
    open,
    initial,
    onFetch,
    onConfirm,
    onFetchMore,
    hasMore
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});
  const intl = useIntl();
  const initialValue = maybe(() => initial.map(({ node }) => node.id), []);
  const initialForm: FormData = {
    allMalls: true,
    malls: initialValue
  };

  const [query, onQueryChange] = useSearchQuery(onFetch);
  const handleSubmit = malls => {
    onConfirm(malls);
  };

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <Form initial={initialForm} onSubmit={handleSubmit}>
        {({ data, change }) => {
          const handleSelectMall = id => {
            if (data.malls.includes(id)) {
              change({
                target: {
                  name: "malls",
                  value: data.malls.filter(mall => mall !== id)
                }
              });
            } else {
              change({
                target: {
                  name: "malls",
                  value: [...data.malls, id]
                }
              });
            }
          };

          const handleSelectAll = event => {
            const { checked } = event.target;
            change({
              target: {
                name: "malls",
                value: checked ? malls.map(mall => mall.id) : []
              }
            });

            change({
              target: {
                name: "allMalls",
                value: checked
              }
            });
          };

          return (
            <>
              <DialogTitle>
                <FormattedMessage
                  defaultMessage="Assign Malls"
                  description="dialog header"
                />
              </DialogTitle>
              <DialogContent>
                <Typography>
                  <FormattedMessage
                    defaultMessage="Choose malls, you want {field} to be limited to, from the list below"
                    values={{ field }}
                  />
                </Typography>
                <FormSpacer />
                <TextField
                  id="filterMallsTxt"
                  name="query"
                  value={query}
                  onChange={onQueryChange}
                  label={intl.formatMessage({
                    defaultMessage: "Filter malls",
                    description: "search box label"
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Search by mall name",
                    description: "search box placeholder"
                  })}
                  fullWidth
                />
              </DialogContent>
              <Hr />
              <DialogContent className={classes.container}>
                <Typography className={classes.heading} variant="subtitle1">
                  <FormattedMessage
                    defaultMessage="Malls A to Z"
                    description="mall selection"
                  />
                </Typography>
                <InfiniteScroll
                  pageStart={0}
                  loadMore={onFetchMore}
                  hasMore={hasMore}
                  useWindow={false}
                  loader={
                    <div
                      className={
                        scrollableDialogClasses.loadMoreLoaderContainer
                      }
                      key={0}
                    >
                      <CircularProgress size={16} />
                    </div>
                  }
                  threshold={10}
                >
                  <ResponsiveTable>
                    <TableBody>
                      <TableRow key="all">
                        <TableCell className={classes.wideCell}>
                          Select All
                        </TableCell>
                        <TableCell
                          padding="checkbox"
                          className={classes.checkboxCell}
                        >
                          <Checkbox
                            checked={
                              malls && malls.length === data.malls.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                      </TableRow>
                      {malls &&
                        malls.map((mall, index) => (
                          <TableRow key={mall.id} id={`mallRow${index}`}>
                            <TableCell className={classes.wideCell}>
                              {mall.name}
                            </TableCell>
                            <TableCell
                              padding="checkbox"
                              className={classes.checkboxCell}
                            >
                              <Checkbox
                                checked={data.malls.includes(mall.id)}
                                id={`mallCheckBoxRow${index}`}
                                onChange={() => handleSelectMall(mall.id)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </ResponsiveTable>
                </InfiniteScroll>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} id="assignMallsBackBtn">
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
                <ConfirmButton
                  idConfirmButton="assignMallsBtn"
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  onClick={() => handleSubmit(data.malls)}
                >
                  <FormattedMessage
                    defaultMessage="Assign malls"
                    description="button"
                  />
                </ConfirmButton>
              </DialogActions>
            </>
          );
        }}
      </Form>
    </Dialog>
  );
};
AssignMallDialog.displayName = "AssignMallDialog";
export default AssignMallDialog;
